import React, { useState, useEffect } from 'react';
import { Drawer, Table, Tag, Typography, Space, Button, Empty, Spin } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { OPMApi } from '../../../../api/opmApi';
import type { ProcessExecutionLogDto, ProcessExecutionStatus } from '../../../../api/opmApi';

const { Text } = Typography;

interface ExecutionHistoryPanelProps {
  visible: boolean;
  onClose: () => void;
  ontologyId: number;
  processNodeId: string;
}

const STATUS_TAG_MAP: Record<string, { color: string; label: string }> = {
  SUCCESS: { color: 'success', label: '成功' },
  FAILED: { color: 'error', label: '失败' },
  RUNNING: { color: 'processing', label: '运行中' },
  TIMEOUT: { color: 'warning', label: '超时' },
  CANCELLED: { color: 'default', label: '已取消' },
  CREATED: { color: 'default', label: '已创建' },
  READY: { color: 'cyan', label: '就绪' },
  RETRYING: { color: 'warning', label: '重试中' },
  TERMINATED: { color: 'error', label: '已终止' },
};

const ExecutionHistoryPanel: React.FC<ExecutionHistoryPanelProps> = ({
  visible,
  onClose,
  ontologyId,
  processNodeId,
}) => {
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState<ProcessExecutionLogDto[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const loadLogs = async (p: number = page, s: number = pageSize) => {
    if (!processNodeId) return;
    setLoading(true);
    try {
      const result = await OPMApi.getProcessExecutionLogs(ontologyId, processNodeId, p, s);
      setLogs(result.content || []);
      setTotal(result.totalElements || 0);
    } catch (error) {
      console.error('加载执行记录失败:', error);
      setLogs([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (visible && processNodeId) {
      loadLogs(0, pageSize);
      setPage(0);
    }
  }, [visible, processNodeId]);

  const handleTableChange = (pagination: any) => {
    const newPage = (pagination.current || 1) - 1;
    const newSize = pagination.pageSize || 10;
    setPage(newPage);
    setPageSize(newSize);
    loadLogs(newPage, newSize);
  };

  const formatPayload = (payload: string | null | undefined): string => {
    if (!payload) return '-';
    try {
      return JSON.stringify(JSON.parse(payload), null, 2);
    } catch {
      return payload;
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 90,
      render: (status: ProcessExecutionStatus) => {
        const info = STATUS_TAG_MAP[status] || { color: 'default', label: status };
        return <Tag color={info.color}>{info.label}</Tag>;
      },
    },
    {
      title: '触发方式',
      dataIndex: 'triggerType',
      key: 'triggerType',
      width: 80,
      render: (type: string) => <Text style={{ fontSize: 12 }}>{type}</Text>,
    },
    {
      title: '耗时',
      dataIndex: 'executionTimeMs',
      key: 'executionTimeMs',
      width: 80,
      render: (ms: number) => (
        <Text style={{ fontFamily: 'monospace', fontSize: 12 }}>{ms}ms</Text>
      ),
    },
    {
      title: '开始时间',
      dataIndex: 'startedAt',
      key: 'startedAt',
      width: 160,
      render: (t: string) => <Text style={{ fontSize: 12 }}>{t || '-'}</Text>,
    },
  ];

  return (
    <Drawer
      title={
        <Space>
          <span>执行记录</span>
          <Text type="secondary" style={{ fontSize: 13, fontWeight: 'normal' }}>
            ({total} 条)
          </Text>
        </Space>
      }
      placement="right"
      width={720}
      open={visible}
      onClose={onClose}
      extra={
        <Button
          icon={<ReloadOutlined />}
          size="small"
          onClick={() => loadLogs(page, pageSize)}
          loading={loading}
        >
          刷新
        </Button>
      }
    >
      <Spin spinning={loading}>
        <Table
          columns={columns}
          dataSource={logs}
          rowKey="id"
          size="small"
          pagination={{
            current: page + 1,
            pageSize,
            total,
            showSizeChanger: true,
            showTotal: (t) => `共 ${t} 条`,
          }}
          onChange={handleTableChange}
          expandable={{
            expandedRowRender: (record) => (
              <div style={{ padding: '8px 0' }}>
                <div style={{ marginBottom: 12 }}>
                  <Text strong style={{ fontSize: 12 }}>输入参数：</Text>
                  <pre
                    style={{
                      background: '#f5f5f5',
                      padding: 8,
                      borderRadius: 4,
                      marginTop: 4,
                      fontSize: 12,
                      fontFamily: 'monospace',
                      maxHeight: 150,
                      overflow: 'auto',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-all',
                    }}
                  >
                    {formatPayload(record.inputPayload)}
                  </pre>
                </div>
                <div style={{ marginBottom: 12 }}>
                  <Text strong style={{ fontSize: 12 }}>输出结果：</Text>
                  <pre
                    style={{
                      background: '#f5f5f5',
                      padding: 8,
                      borderRadius: 4,
                      marginTop: 4,
                      fontSize: 12,
                      fontFamily: 'monospace',
                      maxHeight: 150,
                      overflow: 'auto',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-all',
                      color: record.status === 'FAILED' ? '#ff4d4f' : '#333',
                    }}
                  >
                    {formatPayload(record.outputPayload)}
                  </pre>
                </div>
                {record.stdout && (
                  <div style={{ marginBottom: 12 }}>
                    <Text strong style={{ fontSize: 12 }}>标准输出：</Text>
                    <pre
                      style={{
                        background: '#f5f5f5',
                        padding: 8,
                        borderRadius: 4,
                        marginTop: 4,
                        fontSize: 12,
                        fontFamily: 'monospace',
                        maxHeight: 100,
                        overflow: 'auto',
                      }}
                    >
                      {record.stdout}
                    </pre>
                  </div>
                )}
                {record.stderr && (
                  <div style={{ marginBottom: 12 }}>
                    <Text strong style={{ fontSize: 12, color: '#ff4d4f' }}>错误输出：</Text>
                    <pre
                      style={{
                        background: '#fff2f0',
                        padding: 8,
                        borderRadius: 4,
                        marginTop: 4,
                        fontSize: 12,
                        fontFamily: 'monospace',
                        color: '#ff4d4f',
                        maxHeight: 100,
                        overflow: 'auto',
                      }}
                    >
                      {record.stderr}
                    </pre>
                  </div>
                )}
                {record.errorMessage && (
                  <div>
                    <Text strong style={{ fontSize: 12, color: '#ff4d4f' }}>错误信息：</Text>
                    <Text type="danger" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
                      {record.errorMessage + record.stdout}
                    </Text>
                  </div>
                )}
              </div>
            ),
          }}
        />
      </Spin>
    </Drawer>
  );
};

export default ExecutionHistoryPanel;
